import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

const shallowEqual = (a, b) => {
  const allKeys = _.uniq(_.concat(_.keys(a), ..._.keys(b)));
  return _.every(allKeys, (k) => _.get(a, k) === _.get(b, k));
};

/**
 *
 * @param {object} getListeners key: collection name, value: {
 *   id:string or where:array
 * }, 'where' will be ignored if 'id' is provided. or function which returns this from of object, parameter should be own props
 * @param mapDataToProps
 * @returns {function(*)}
 */
const connectFirebase = (getListeners, mapDataToProps) => (Comp) => {
  getListeners = getListeners || {};
  return class extends React.Component {
    _unsubscribers = [];

    _localsChangeListener = (locals) => this.setState({ locals });

    static contextTypes = {
      firebase: PropTypes.object,
      localsObserver: PropTypes.object,
    };

    shouldComponentUpdate(nextProps, nextState, nextContext) {
      return !shallowEqual(
        this._getChildProps(
          this.props,
          this.context.firebase,
          this.state.data,
          this.state.locals,
          mapDataToProps
        ),
        this._getChildProps(
          nextProps,
          nextContext.firebase,
          nextState.data,
          nextState.locals,
          mapDataToProps
        )
      );
    }

    _onSnapshot = (collectionName) => (snapshot) => {
      let newState = {
        data: {
          ...this.state.data,
          [collectionName]: {
            ...this.state.data[collectionName],
          },
        },
      };

      snapshot.docChanges().forEach((change) => {
        switch (change.type) {
          case 'added':
          case 'modified':
            newState.data[collectionName][change.doc.id] = {
              id: change.doc.id,
              ...change.doc.data({
                serverTimestamps: 'estimate',
              }),
            };
            break;
          case 'removed':
            newState.data[collectionName] = _.omit(
              newState.data[collectionName],
              change.doc.id
            );
            break;
        }
      });

      this.setState(newState);
    };

    constructor(props, context) {
      super(props, context);
      const listeners =
        typeof getListeners === 'function'
          ? getListeners({
              firebase: context.firebase,
              locals: context.localsObserver.getCurrentLocals(),
              ownProps: props,
            })
          : getListeners;
      this.state = {
        locals: context.localsObserver.getCurrentLocals(),
        data: _.mapValues(listeners, () => ({})), // 각 collection name별로 빈 객체 넣어서 초기화
      };
    }

    _registerListeners = (props) => {
      const db = this.context.firebase.firestore();
      const listeners =
        typeof getListeners === 'function'
          ? getListeners({
              firebase: this.context.firebase,
              locals: this.state.locals,
              ownProps: props,
            })
          : getListeners;

      _.forEach(listeners, (value, collectionName) => {
        if (typeof value === 'function') {
          this._unsubscribers.push(
            value().onSnapshot(this._onSnapshot(collectionName))
          );
          return;
        }
        const { id, where } = value;
        if (!!id) {
          const unsubscriber = db
            .collection(collectionName)
            .doc(id)
            .onSnapshot((doc) => {
              this.setState({
                data: {
                  ...this.state.data,
                  [collectionName]: {
                    ...this.state[collectionName],
                    [id]: {
                      id,
                      ...doc.data({
                        serverTimestamps: 'estimate',
                      }),
                    },
                  },
                },
              });
            });

          this._unsubscribers.push(unsubscriber);
        } else {
          let ref = db.collection(collectionName);

          // 함수도 아니고 배열도 아니면 전체
          if (typeof where === 'function') ref = where(ref);
          else if (where instanceof Array) ref = ref.where(...where);

          const unsubscriber = ref.onSnapshot(this._onSnapshot(collectionName));

          this._unsubscribers.push(unsubscriber);
        }
      });
    };

    _unregisterListeners = () => {
      this._unsubscribers.forEach((u) => u());
      this._unsubscribers = [];
    };

    componentDidMount() {
      this.context.localsObserver.addLocalsChangedListener(
        this._localsChangeListener
      );
      this._registerListeners(this.props);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (shallowEqual(this.props, nextProps)) return;
      this._unregisterListeners();
      this._registerListeners(nextProps);
    }

    UNSAFE_componentWillUnmount() {
      this._unregisterListeners();
      this.context.localsObserver.removeLocalsChangedListener(
        this._localsChangeListener
      );
    }

    _getChildProps = (props, firebase, data, locals, mapDataToProps) => {
      return {
        ...props,
        ...(mapDataToProps
          ? mapDataToProps({
              data,
              firebase,
              locals,
              ownProps: props,
            })
          : data),
      };
    };

    render() {
      return (
        <Comp
          {...this._getChildProps(
            this.props,
            this.context.firebase,
            this.state.data,
            this.state.locals,
            mapDataToProps
          )}
        />
      );
    }
  };
};

export default connectFirebase;
